'use client';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { ChildImageVariantEnum } from '@sola/ark-core/utils/child-image.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import {
  Lightbox,
  LightboxTrigger,
} from '@sola/ark-skippy/molecules/lightbox/lightbox.tsx';
import { getChildBioPath, knownMyUrls } from '@sola/my-sdk/constants.ts';

import { BackButton } from '../../atoms/back-button/back-button.tsx';
import { usePreloadNamespaces } from '../../utils/use-preload-namespaces.ts';
import {
  ChildSpotlightCard,
  ChildSpotlightImage,
} from '../child-spotlight-card/child-spotlight-card.tsx';
import { useDepartureSpotlightCard } from './departure-spotlight-card.logic.tsx';

const departureVideoUrl = 'https://vimeo.com/1227414994/77b0290152';

export const DepartureSpotlightCard = () => {
  usePreloadNamespaces('skippy');
  const {
    t,
    hasGlobalId,
    hasMultipleSponsorships,
    isLoading,
    hasError,
    cardData,
  } = useDepartureSpotlightCard();

  if (!hasGlobalId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="DeparturePage__card flex w-full flex-col gap-comfy-xl">
        <Skeleton height={24} width={180} />
        <Skeleton height={540} width="100%" borderRadius="1rem" />
      </div>
    );
  }

  if (hasError || !cardData) {
    return (
      <Alert
        variant="negative"
        title={t('my:sponsorshipRelationship.childBio.alert.title')}
        message={t('my:sponsorshipRelationship.childBio.alert.message')}
      />
    );
  }

  const backButtonLabel = hasMultipleSponsorships
    ? t('my:sponsorshipRelationship.childBio.backButton.text')
    : t('my:sponsorshipRelationship.childBio.detailsTitle', {
        childName: cardData.childName,
      });
  const backButtonUrl = hasMultipleSponsorships
    ? knownMyUrls.sponsorshipsBase
    : getChildBioPath(cardData.childGlobalId);
  const hasSponsorYears = cardData.sponsorYears !== undefined;
  const watchVideoLabel = t(
    'my:sponsorshipRelationship.departures.spotlight.departure.primaryAction',
  );

  return (
    <div className="flex w-full flex-col gap-comfy-lg">
      <BackButton url={backButtonUrl} label={backButtonLabel} />
      <ChildSpotlightCard
        image={
          <ChildSpotlightImage
            childImageUrl={cardData.childImageUrl}
            alt={cardData.childName}
            variantMd={ChildImageVariantEnum.Full}
          />
        }
      >
        <div className="flex h-full flex-col justify-center gap-comfy-lg md:p-roomy-xs">
          <div className="ChildSpotlightCard__text flex w-full flex-col gap-comfy-xs">
            <h2 className="ChildSpotlightCard__title m-none w-full heading-neutral-amplified heading-sm md:heading-md">
              {t(cardData.titleKey, { childName: cardData.childName })}
            </h2>
            <div className="ChildSpotlightCard__description w-full body-neutral-mild body-sm md:body-md">
              <p>
                {t(
                  hasSponsorYears
                    ? 'my:sponsorshipRelationship.departures.spotlight.departure.descriptionIntro'
                    : 'my:sponsorshipRelationship.departures.spotlight.departure.descriptionIntroFallback',
                  {
                    childName: cardData.childName,
                    ...(hasSponsorYears
                      ? {
                          context: cardData.gender ?? undefined,
                          sponsorYears: cardData.sponsorYears,
                        }
                      : {}),
                  },
                )}
              </p>
              <p>
                {t(
                  'my:sponsorshipRelationship.departures.spotlight.departure.descriptionVideo',
                )}
              </p>
            </div>
          </div>
          <Lightbox
            items={[
              {
                title: watchVideoLabel,
                media: {
                  video: { url: departureVideoUrl, title: watchVideoLabel },
                },
              },
            ]}
          >
            <LightboxTrigger className="w-fit" ariaLabel={watchVideoLabel}>
              <ButtonStyle
                iconBefore={<ArkIcon iconName="play-circle" />}
                variant="secondary"
              >
                <span>{watchVideoLabel}</span>
              </ButtonStyle>
            </LightboxTrigger>
          </Lightbox>
        </div>
      </ChildSpotlightCard>
    </div>
  );
};
